/**
 * TechnicalIndicators Component
 *
 * Collapsible panel showing combined daily and intraday technical indicators.
 * Each indicator shows name, value, and bullish/bearish/neutral color coding.
 */

import { useState } from 'react';
import './TechnicalIndicators.css';

// Indicator definitions with thresholds for bullish/bearish classification
const INDICATOR_DEFS = {
  // Daily indicators
  rsi_14: {
    label: 'RSI (14)',
    tooltip: 'Relative Strength Index - 14 period. <30 oversold, >70 overbought',
    classify: (val) => val < 30 ? 'bullish' : val > 70 ? 'bearish' : 'neutral',
    format: (val) => val?.toFixed(1)
  },
  macd_histogram: {
    label: 'MACD Histogram',
    tooltip: 'MACD line minus signal line. Positive = bullish momentum',
    classify: (val) => val > 0 ? 'bullish' : val < 0 ? 'bearish' : 'neutral',
    format: (val) => val?.toFixed(3)
  },
  pct_from_ema_50: {
    label: '% from EMA-50',
    tooltip: 'Distance from 50-day exponential moving average',
    classify: (val) => val > 2 ? 'bullish' : val < -5 ? 'bearish' : 'neutral',
    format: (val) => `${val?.toFixed(1)}%`
  },
  adx: {
    label: 'ADX',
    tooltip: 'Average Directional Index. >25 = strong trend',
    classify: () => 'neutral', // ADX doesn't indicate direction
    format: (val) => val?.toFixed(1)
  },
  stoch_k: {
    label: 'Stochastic %K',
    tooltip: 'Stochastic oscillator. <20 oversold, >80 overbought',
    classify: (val) => val < 20 ? 'bullish' : val > 80 ? 'bearish' : 'neutral',
    format: (val) => val?.toFixed(1)
  },
  stoch_d: {
    label: 'Stochastic %D',
    tooltip: 'Stochastic signal line (3-period SMA of %K)',
    classify: (val) => val < 20 ? 'bullish' : val > 80 ? 'bearish' : 'neutral',
    format: (val) => val?.toFixed(1)
  },

  // Intraday indicators
  rsi_7: {
    label: 'RSI (7) Intraday',
    tooltip: 'Short-term RSI for intraday momentum. <30 oversold',
    classify: (val) => val < 30 ? 'bullish' : val > 70 ? 'bearish' : 'neutral',
    format: (val) => val?.toFixed(1)
  },
  pct_from_low: {
    label: 'Bounce from Low',
    tooltip: 'How much price has recovered from intraday low',
    classify: (val) => val > 1.5 ? 'bullish' : val < 0.5 ? 'bearish' : 'neutral',
    format: (val) => `+${val?.toFixed(2)}%`
  },
  above_vwap: {
    label: 'Above VWAP',
    tooltip: 'Price above Volume-Weighted Average Price (bullish)',
    classify: (val) => val ? 'bullish' : 'bearish',
    format: (val) => val ? 'Yes' : 'No'
  },
  recovery_confidence: {
    label: 'Recovery Confidence',
    tooltip: 'ML model confidence in intraday recovery pattern',
    classify: (val) => val >= 0.65 ? 'bullish' : val >= 0.5 ? 'neutral' : 'bearish',
    format: (val) => `${Math.round((val || 0) * 100)}%`
  }
};

function IndicatorItem({ name, value, def }) {
  const [showTooltip, setShowTooltip] = useState(false);

  if (value == null) return null;

  const classification = def.classify(value);
  const formattedValue = def.format(value);

  return (
    <div
      className={`indicator-item indicator-${classification}`}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div className="indicator-name">
        {def.label}
        <span className="info-icon">ℹ️</span>
      </div>
      <div className="indicator-value">{formattedValue}</div>
      {showTooltip && (
        <div className="indicator-tooltip">{def.tooltip}</div>
      )}
    </div>
  );
}

function TechnicalIndicators({ dailyData = {}, intradayData = {}, keyFeatures = {} }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = () => setIsExpanded(!isExpanded);

  // Combine data sources
  const dailyIndicators = {
    rsi_14: keyFeatures.rsi_14 || dailyData.rsi_14,
    macd_histogram: keyFeatures.macd_histogram || dailyData.macd_histogram,
    pct_from_ema_50: keyFeatures.pct_from_ema_50 || dailyData.pct_from_ema_50,
    adx: keyFeatures.adx || dailyData.adx,
    stoch_k: keyFeatures.stoch_k || dailyData.stoch_k,
    stoch_d: keyFeatures.stoch_d || dailyData.stoch_d
  };

  const intradayIndicators = {
    rsi_7: intradayData.key_signals?.rsi_7 || intradayData.rsi_7,
    pct_from_low: intradayData.pct_from_low,
    above_vwap: intradayData.key_signals?.above_vwap || intradayData.above_vwap,
    recovery_confidence: intradayData.recovery_confidence
  };

  // Check if we have any data
  const hasDaily = Object.values(dailyIndicators).some(v => v != null);
  const hasIntraday = Object.values(intradayIndicators).some(v => v != null);

  if (!hasDaily && !hasIntraday) {
    return null;
  }

  return (
    <div className="technical-indicators">
      {/* Collapsible Header */}
      <div className="technical-indicators-header" onClick={toggleExpanded}>
        <h3>Technical Indicators</h3>
        <span className="expand-icon">{isExpanded ? '▲' : '▼'}</span>
      </div>

      {/* Collapsible Content */}
      {isExpanded && (
        <div className="technical-indicators-content">
          {/* Daily Indicators */}
          {hasDaily && (
            <div className="indicator-section">
              <h4>Daily (Close)</h4>
              <div className="indicators-grid">
                {Object.entries(dailyIndicators).map(([key, value]) => {
                  const def = INDICATOR_DEFS[key];
                  if (!def || value == null) return null;
                  return (
                    <IndicatorItem
                      key={key}
                      name={key}
                      value={value}
                      def={def}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* Intraday Indicators */}
          {hasIntraday && (
            <div className="indicator-section">
              <h4>Intraday</h4>
              <div className="indicators-grid">
                {Object.entries(intradayIndicators).map(([key, value]) => {
                  const def = INDICATOR_DEFS[key];
                  if (!def || value == null) return null;
                  return (
                    <IndicatorItem
                      key={key}
                      name={key}
                      value={value}
                      def={def}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* Legend */}
          <div className="indicator-legend">
            <span className="legend-item legend-bullish">● Bullish</span>
            <span className="legend-item legend-neutral">● Neutral</span>
            <span className="legend-item legend-bearish">● Bearish</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default TechnicalIndicators;
